/** A status path: chevron steps in order, each done, current, blocked, ahead or not checked.
 *
 * The record page's "where this stands" (Uploaded, Read, Reviewed, Verified, In ledger) and the
 * filing's path (`PathToFiling`) are the same drawing. **State is a shape and a word, never colour
 * alone** (design rule 4): every state has its own glyph and every step carries its state in text
 * (`STATE_WORD`), visible as a title on the current step and read by a screen reader on all. The
 * steps are statements the server made (`screens/filingSpine.ts` says which for a filing), so this
 * component only draws: it decides nothing and counts nothing.
 *
 * The current step is drawn larger when `emphasis="current"` (the filing path); the record page's
 * path keeps them one size.
 */

import {
  CheckCircle,
  Circle,
  CircleNotch,
  MinusCircle,
  Question,
  XCircle,
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";

import { STATE_WORD } from "../../screens/filingSpine";
import type { SpineStep, StepState } from "../../screens/filingSpine";
import styles from "./StatusPath.module.css";

const GLYPH: Record<StepState, { icon: Icon; weight: "fill" | "bold" | "regular" }> = {
  done: { icon: CheckCircle, weight: "fill" },
  current: { icon: CircleNotch, weight: "bold" },
  blocked: { icon: XCircle, weight: "fill" },
  ahead: { icon: Circle, weight: "regular" },
  unknown: { icon: Question, weight: "bold" },
  na: { icon: MinusCircle, weight: "regular" },
};

export function StatusPath({
  steps,
  label,
  emphasis = "none",
  testId,
}: {
  steps: readonly SpineStep[];
  /** What the list is, for a screen reader: "Where this record stands". */
  label: string;
  emphasis?: "none" | "current";
  testId?: string;
}) {
  return (
    <ol
      className={`${styles.path} ${emphasis === "current" ? styles.emphasised : ""}`}
      aria-label={label}
      data-testid={testId}
    >
      {steps.map((step) => {
        const { icon: Glyph, weight } = GLYPH[step.state];
        return (
          <li
            key={step.key}
            className={styles.step}
            data-state={step.state}
            data-step={step.key}
            data-here={step.here ? "" : undefined}
          >
            <span className={styles.chevron}>
              <Glyph size={16} weight={weight} aria-hidden="true" className={styles.mark} />
              <span className={styles.label} title={step.label}>
                {step.label}
              </span>
              <span className="visuallyHidden">: {STATE_WORD[step.state]}</span>
              {step.note ? <span className="visuallyHidden">, {step.note}</span> : null}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
